import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExportMapModal } from '../../store/actions/modals';
import { basemapSelector } from '../../store/selectors/general';
import { layerVisSelector } from '../../store/selectors/layers';
import { cropBoundsSelector } from '../../store/selectors/map';
import { captureMap, initDownload } from '../../utils/exportMapUtils';
import { Button } from '../general/Button';
import { ModalWrapper } from './ModalWrapper';
import { trackEvent, MAP_DOWNLOAD } from '../../utils/googleAnalytics';
import clsx from 'clsx';

const CROP_LAYER_IDS = ['crop-tool-fill', 'crop-tool-outline'];

export const ExportMapModal = ({ mapRef }) => {
  const dispatch = useDispatch();
  const basemap = useSelector(basemapSelector);
  const layerVisibility = useSelector(layerVisSelector);
  const cropBounds = useSelector(cropBoundsSelector);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const debounceTimer = useRef(null);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [format, setFormat] = useState('png');
  const [hasInitiatedDownload, setHasInitiatedDownload] = useState(false);
  const previewRequestIdRef = useRef(0);
  const cropBoundsRef = useRef(cropBounds);
  const formatRef = useRef(format);

  cropBoundsRef.current = cropBounds;
  formatRef.current = format;

  const visibleLayerIds = useMemo(
    () =>
      Object.entries(layerVisibility || {})
        .filter(([, isVisible]) => Boolean(isVisible))
        .map(([layerId]) => layerId),
    [layerVisibility],
  );

  const generatePreview = useCallback(
    async (titleText, exportFormat = formatRef.current) => {
      const requestId = ++previewRequestIdRef.current;
      setIsLoading(true);

      try {
        const dataUrl = await captureMap(
          mapRef,
          titleText,
          exportFormat,
          cropBoundsRef.current,
          visibleLayerIds,
          basemap,
        );

        if (requestId !== previewRequestIdRef.current) {
          return;
        }

        setPreviewUrl(dataUrl);
      } catch (error) {
        if (requestId === previewRequestIdRef.current) {
          console.error('Failed to generate preview:', error);
        }
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [mapRef, visibleLayerIds, basemap],
  );

  // Hide crop overlay once while modal is open (restored on unmount).
  useEffect(() => {
    const map = mapRef.current?.getMap?.() ?? mapRef.current;
    if (!map) return undefined;

    const previousVisibilityByLayerId = {};
    CROP_LAYER_IDS.forEach(layerId => {
      if (map.getLayer(layerId)) {
        previousVisibilityByLayerId[layerId] = map.getLayoutProperty(layerId, 'visibility');
        map.setLayoutProperty(layerId, 'visibility', 'none');
      }
    });

    return () => {
      CROP_LAYER_IDS.forEach(layerId => {
        if (map.getLayer(layerId) && Object.hasOwn(previousVisibilityByLayerId, layerId)) {
          map.setLayoutProperty(
            layerId,
            'visibility',
            previousVisibilityByLayerId[layerId] ?? 'visible',
          );
        }
      });
    };
  }, [mapRef]);

  useEffect(() => {
    generatePreview('');
  }, [generatePreview]);

  useEffect(
    () => () => {
      previewRequestIdRef.current += 1;
    },
    [],
  );

  const handleTitleChange = e => {
    const value = e.target.value;
    setTitle(value);
    setIsDebouncing(true);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setIsDebouncing(false);
      generatePreview(value);
    }, 500);
  };

  const handleFormatChange = e => {
    const newFormat = e.target.value;
    setFormat(newFormat);
    generatePreview(title, newFormat);
  };

  const exportMap = async () => {
    if (!previewUrl) return;
    const filename = title ? `${title}-map-export.${format}` : `map-export.${format}`;

    trackEvent(MAP_DOWNLOAD, { format, filename });

    setIsLoading(true);
    try {
      initDownload(previewUrl, filename);
      setHasInitiatedDownload(true);
    } catch (error) {
      console.error('Failed to export map:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalWrapper large={false} medium={true} customClose={true}>
      <h1 className="mb-6 font-bold">Export Map</h1>
      <div className="grid grid-cols-4 grid-rows-2 gap-4">
        <label>Name your map: </label>
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          placeholder="Enter map title..."
          className="col-span-3"
        />
        <label>Format: </label>
        <select value={format} onChange={handleFormatChange} className="col-span-1">
          <option value="png">PNG</option>
          <option value="jpeg">JPEG</option>
        </select>
      </div>
      {isLoading && <p>Generating preview...</p>}
      {previewUrl && (
        <div className="relative my-4 flex w-full justify-center">
          <img
            src={previewUrl}
            alt="Map preview"
            className={clsx(
              'max-w-4/5 h-auto max-h-[375px] border-2 border-gray-300 object-contain',
              isLoading || isDebouncing ? 'opacity-50' : 'opacity-100',
            )}
          />
          {(isDebouncing || isLoading) && (
            <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white bg-opacity-70">
              <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-teal" />
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end gap-4">
        <Button onClick={() => dispatch(setExportMapModal(false))} disabled={isLoading} inverted>
          Cancel
        </Button>
        {!hasInitiatedDownload && (
          <Button onClick={exportMap} disabled={isLoading}>
            Export
          </Button>
        )}
        {hasInitiatedDownload && (
          <Button onClick={() => dispatch(setExportMapModal(false))} disabled={isLoading}>
            Done
          </Button>
        )}
      </div>
    </ModalWrapper>
  );
};
